import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CHART_PALETTE } from '../../../theme/chartColors';

const BarChartRenderer = ({ data, config, compact, stacked = false }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="dc-no-data">No bar data available</div>;
  }

  const { xKey, yKeys = [], yLabels = [], colors = CHART_PALETTE, layout = 'vertical', showLegend = true, showGrid = true } = config || {};
  const isHorizontal = layout === 'horizontal' || config?.horizontal;
  const isStacked = stacked || config?.stacked;

  // Robust detection
  const actualXKey = data[0][xKey] !== undefined ? xKey : Object.keys(data[0]).find(k => typeof data[0][k] === 'string') || 'name';
  const actualYKeys = yKeys.length > 0 && data[0][yKeys[0]] !== undefined ? yKeys : [Object.keys(data[0]).find(k => typeof data[0][k] === 'number') || 'value'];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={compact ? { top: 0, right: 10, left: 40, bottom: 80 } : { top: 10, right: 30, left: 50, bottom: 120 }}
      >
        {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={!isHorizontal} horizontal={isHorizontal} stroke="var(--border-primary)" />}
        
        {!isHorizontal ? (
          <>
            <XAxis 
              dataKey={actualXKey} 
              interval={0}
              angle={-45}
              textAnchor="end"
              height={compact ? 80 : 130}
              tick={{ fontSize: compact ? 9 : 11, fill: 'var(--text-tertiary)' }} 
              axisLine={false} 
              tickLine={false} 
            />
            <YAxis tick={{ fontSize: compact ? 10 : 12, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
          </>
        ) : (
          <>
            <XAxis type="number" tick={{ fontSize: compact ? 10 : 12, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
            <YAxis dataKey={actualXKey} type="category" width={100} tick={{ fontSize: compact ? 10 : 12, fill: 'var(--text-primary)' }} axisLine={false} tickLine={false} />
          </>
        )}

        <Tooltip
          cursor={false}
          allowEscapeViewBox={{ x: false, y: true }}
          contentStyle={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: '8px', zIndex: 1000 }}
          itemStyle={{ color: 'var(--text-primary)' }}
        />
        {showLegend && (!compact || yKeys.length > 1) && (
          <Legend 
            verticalAlign="top"
            align="center"
            iconType="circle"
            wrapperStyle={{ 
              fontSize: '10px', 
              paddingBottom: '20px',
              visibility: (compact && yKeys.length <= 1) ? 'hidden' : 'visible' 
            }} 
          />
        )}
        
        {actualYKeys.map((key, index) => (
          <Bar 
            key={key} 
            dataKey={key} 
            name={yLabels[index] || key} 
            stackId={isStacked ? 'a' : undefined}
            fill={colors[index % colors.length]} 
            radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} 
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BarChartRenderer;
